import { useState, useCallback, useMemo } from 'react';
import { pdfjs } from 'react-pdf';
import { getInsuranceDocumentViewUrl, fetchPdfDocument } from '../../../services/api/InsuranceService';
import { DOCUMENT_TYPE, INSURANCE_TYPE } from '../../../utils/insuranceTypeUtils';

// PDF.js Workerの設定
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

/**
 * PDF取得のタイムアウト時間（ミリ秒）
 * PDFファイルサイズが大きい場合やネットワーク遅延を考慮した設定値
 */
const PDF_FETCH_TIMEOUT_MS = 30000;

/**
 * PDF読み込みと表示を管理するカスタムフック
 * 
 * react-pdf を使用してPDFドキュメントの読み込み、表示、エラー処理を管理する。
 * 引受目安詳細画面でPDFビューアとして使用される。
 * 
 * @param {Object} options - オプション
 * @param {Function} options.onError - エラー発生時のコールバック
 * @param {Function} options.setIsRendering - レンダリング状態更新関数
 * @returns {Object} PDF関連の状態とハンドラ
 * @returns {boolean} returns.loading - PDF読み込み中かどうか
 * @returns {ArrayBuffer|null} returns.pdfFile - PDFファイルデータ
 * @returns {boolean} returns.showErrorPage - エラーページを表示するか
 * @returns {string} returns.errorMessage - エラーメッセージ
 * @returns {boolean} returns.isLandscape - ページが横向きかどうか
 * @returns {Object|null} returns.pageInfo - ページ情報（width, height）
 * @returns {Object} returns.pdfOptions - react-pdf用の設定オブジェクト
 * @returns {Function} returns.setLoading - ローディング状態設定関数
 * @returns {Function} returns.loadPdfDocument - PDF読み込み関数
 * @returns {Function} returns.onDocumentLoadSuccess - ドキュメント読み込み成功コールバック
 * @returns {Function} returns.onDocumentLoadError - ドキュメント読み込みエラーコールバック
 * @returns {Function} returns.onPageLoadSuccess - ページ読み込み成功コールバック
 * @returns {Function} returns.onPageLoadError - ページ読み込みエラーコールバック
 * @returns {Function} returns.resetPdfState - 状態リセット関数
 * @returns {Function} returns.handleError - エラーハンドリング関数
 */
export const usePdfViewer = ({ onError, setIsRendering }) => {
  /**
   * PDF読み込み中フラグ
   * 初期値はtrue（初期ロード状態）
   */
  const [loading, setLoading] = useState(true);

  /**
   * PDFファイルデータ（ArrayBuffer）
   * APIから取得したPDFバイナリデータを保持
   */
  const [pdfFile, setPdfFile] = useState(null);

  /**
   * PDFDocumentProxy
   * react-pdf の onLoadSuccess で取得したドキュメントオブジェクト
   * キーワード検索時のページテキスト抽出に使用
   */
  const [pdfDocument, setPdfDocument] = useState(null);

  /**
   * エラーページ表示フラグ
   * trueの場合、PDFビューアの代わりにエラーページを表示
   */
  const [showErrorPage, setShowErrorPage] = useState(false);

  /**
   * エラーメッセージ
   * ユーザーに表示するエラー内容
   */
  const [errorMessage, setErrorMessage] = useState('');

  /**
   * ページ横向きフラグ
   * 幅 > 高さの場合にtrue
   */
  const [isLandscape, setIsLandscape] = useState(false);

  /**
   * ページ情報
   * ハイライト描画用にページサイズを保持
   * { width: number, height: number }
   */
  const [pageInfo, setPageInfo] = useState(null);

  /**
   * react-pdf 用の設定オブジェクト
   * CJK文字（日本語等）表示のためのcMap設定を含む
   */
  const pdfOptions = useMemo(
    () => ({
      cMapUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/cmaps/`,
      cMapPacked: true,
      standardFontDataUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/standard_fonts/`,
    }),
    [],
  );

  /**
   * エラーハンドリング
   * エラーメッセージを設定し、エラーページを表示する
   * 
   * @param {string} message - エラーメッセージ
   * @param {Error|null} error - エラーオブジェクト（オプション）
   */
  const handleError = useCallback((message, error = null) => {
    const errorDetail = error ? `: ${error.message || '不明なエラー'}` : '';
    setErrorMessage(`${message}${errorDetail}`);
    setShowErrorPage(true);
    setLoading(false);
    if (onError) {
      onError(message, error);
    }
  }, [onError]);

  /**
   * PDFドキュメントを読み込む
   * APIからPDFを取得し、pdfFile stateにセットする
   * タイムアウト付きでfetchを実行
   * 
   * @param {string} blobName - PDFのBlob名（ストレージ上のファイル名）
   * @param {Object} detailResult - 検索結果の詳細データ
   */
  const loadPdfDocument = useCallback(async (blobName, detailResult) => {
    if (!blobName || !detailResult) {
      return;
    }

    try {
      setLoading(true);

      // PDFのURLを生成
      const documentUrl = getInsuranceDocumentViewUrl(blobName);
      if (!documentUrl) {
        throw new Error('PDFのURLが無効です');
      }

      // APIパラメータを設定
      const params = {
        insurance_type: INSURANCE_TYPE.LIFE,
        document_type: DOCUMENT_TYPE.HIKIUKE,
      };

      // タイムアウト付きでPDFを取得
      const fetchPromise = fetchPdfDocument(documentUrl, params);
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('PDFの取得がタイムアウトしました')), PDF_FETCH_TIMEOUT_MS),
      );

      const arrayBuffer = await Promise.race([fetchPromise, timeoutPromise]);

      // レスポンスの検証
      if (!arrayBuffer) {
        throw new Error('APIからの応答がありません');
      }
      if (arrayBuffer.byteLength === 0) {
        throw new Error('PDFデータが空です');
      }

      // 正常にPDFを取得できた場合
      setPdfFile(arrayBuffer);
      setShowErrorPage(false);
      setLoading(false);
      if (setIsRendering) {
        setIsRendering(true);
      }
    } catch (error) {
      handleError('PDFビューワー設定エラー', error);
    }
  }, [handleError, setIsRendering]);

  /**
   * PDFドキュメント読み込み成功時のコールバック
   * react-pdfのDocument componentから呼ばれる
   * 
   * @param {Object} pdf - PDFドキュメント情報
   * @param {number} pdf.numPages - 総ページ数
   * @param {Function} setTotalPages - 総ページ数設定関数
   */
  const onDocumentLoadSuccess = useCallback((pdfDoc, setTotalPages) => {
    setPdfDocument(pdfDoc);
    if (setTotalPages) {
      setTotalPages(pdfDoc.numPages);
    }
    setLoading(false);
    setShowErrorPage(false);
  }, []);

  /**
   * PDFドキュメント読み込みエラー時のコールバック
   * react-pdfのDocument componentから呼ばれる
   * 
   * @param {Error} error - エラーオブジェクト
   */
  const onDocumentLoadError = useCallback((error) => {
    handleError('PDFの読み込みに失敗しました', error);
  }, [handleError]);

  /**
   * PDFページ読み込み成功時のコールバック
   * react-pdfのPage componentから呼ばれる
   * ページの向き（横/縦）とサイズ情報を取得
   * 
   * @param {Object} page - PDFページオブジェクト
   */
  const onPageLoadSuccess = useCallback(async (page) => {
    if (setIsRendering) {
      setIsRendering(false);
    }

    // ページのビューポート情報を取得
    const viewport = page.getViewport({ scale: 1 });
    const isPageLandscape = viewport.width > viewport.height;
    setIsLandscape(isPageLandscape);

    // ハイライト描画用に pageInfo を保存
    setPageInfo({
      width: viewport.width,
      height: viewport.height,
    });

    // テキストコンテンツを事前取得（テキスト選択機能用）
    try {
      await page.getTextContent();
    } catch {
      // ignore
    }
  }, [setIsRendering]);

  /**
   * PDFページ読み込みエラー時のコールバック
   * react-pdfのPage componentから呼ばれる
   * 
   * @param {Error} error - エラーオブジェクト
   * @param {number} currentPage - 読み込みに失敗したページ番号
   */
  const onPageLoadError = useCallback((error, currentPage) => {
    setErrorMessage(`ページ ${currentPage} の読み込みに失敗しました: ${error.message}`);
  }, []);

  /**
   * PDF状態をリセットする
   * 再実行時に使用
   */
  const resetPdfState = useCallback(() => {
    setLoading(true);
    setShowErrorPage(false);
    setErrorMessage('');
    setPdfFile(null);
    setPageInfo(null);
    setPdfDocument(null);
  }, []);

  return {
    // 状態
    loading,
    pdfFile,
    pdfDocument,
    showErrorPage,
    errorMessage,
    isLandscape,
    pageInfo,
    pdfOptions,
    // 状態設定関数
    setLoading,
    // PDF読み込み
    loadPdfDocument,
    // react-pdfコールバック
    onDocumentLoadSuccess,
    onDocumentLoadError,
    onPageLoadSuccess,
    onPageLoadError,
    // ユーティリティ
    resetPdfState,
    handleError,
  };
};

export default usePdfViewer;
